import { localizedStringForLocale } from "src/utils/mapEntertainment";

/**
 * @typedef {Object} PastEventTableRow
 * @property {string} [image] - CDN-relative path; `""` renders no winner-photo modal link
 * @property {string} [winner]
 * @property {string} [tournament] - localized; row column hidden when absent
 * @property {string} [event] - optional row label (e.g. "#1", "Final"); UI falls back to `#N` / `No. N`
 * @property {string} [name] - legacy 2013 payout-leaderboard shape
 * @property {string} [country] - legacy 2013 payout-leaderboard shape
 * @property {string} [winnings] - legacy 2013 payout-leaderboard shape
 */

/**
 * @typedef {Object} PastEvent
 * @property {string|number} [id] - Payload document id
 * @property {string} [slug] - localized; winners-page lookup key (`:eventSlug` route param)
 * @property {number} [year]
 * @property {string} title
 * @property {string} date - free-form display string (legacy `date`, CMS `dateLabel`)
 * @property {string} [cart_image]
 * @property {string} [url] - localized path; list-card href + static-fallback match
 * @property {string} [image]
 * @property {string} [image_mobile]
 * @property {string} [logo]
 * @property {Object|null} winnerDetail
 * @property {PastEventTableRow[]} table
 */

const localized = (raw, locale) => localizedStringForLocale(raw, locale) || undefined;

/**
 * Maps a Payload `past-events` document to the legacy static-JSON shape consumed by
 * PastEvents.jsx / PastEventWinners.jsx (plus `slug` and `year`).
 *
 * Fidelity rules (see docs/PAST_EVENTS_CMS_INTEGRATION_PLAN.md "Data audit"):
 * - `prize` stays a string (rendered via formatAmount)
 * - optional/empty table fields are preserved, never dropped
 * - legacy `{name, country, winnings}` rows pass through untouched
 * - `winnerDetail` is `null` when the doc has none
 *
 * @param {Record<string, unknown>} doc - Payload REST document
 * @param {'en' | 'fr'} [locale]
 * @returns {PastEvent}
 */
export function mapPastEvent(doc, locale = "en") {
  const wd =
    doc.winnerDetail && typeof doc.winnerDetail === "object"
      ? doc.winnerDetail
      : null;

  const winnerDetail = wd
    ? {
        name: wd.name,
        event: localized(wd.event, locale),
        image: wd.image || undefined,
        prize: wd.prize != null && wd.prize !== "" ? String(wd.prize) : undefined,
        origin: localized(wd.origin, locale),
        country: wd.country,
      }
    : null;

  const table = Array.isArray(doc.table)
    ? doc.table.map((row) => ({
        image: row.image ?? "",
        winner: row.winner || undefined,
        tournament: localized(row.tournament, locale),
        event: row.event || undefined,
        name: row.name || undefined,
        country: row.country || undefined,
        winnings: row.winnings || undefined,
      }))
    : [];

  return {
    id: doc.id != null ? doc.id : undefined,
    slug: localized(doc.slug, locale),
    year: typeof doc.year === "number" ? doc.year : undefined,
    title: localizedStringForLocale(doc.title, locale),
    date: localizedStringForLocale(doc.dateLabel, locale),
    cart_image: localized(doc.cartImage, locale),
    url: localized(doc.url, locale),
    image: localized(doc.headerImage, locale),
    image_mobile: localized(doc.headerImageMobile, locale),
    logo: localized(doc.logo, locale),
    winnerDetail,
    table,
  };
}

/**
 * @param {{ docs?: unknown[] }} payloadResponse
 * @param {'en' | 'fr'} [locale]
 * @returns {PastEvent[]}
 */
export function mapPastEventsResponse(payloadResponse, locale = "en") {
  const docs = Array.isArray(payloadResponse?.docs) ? payloadResponse.docs : [];
  return docs.map((doc) => mapPastEvent(doc, locale));
}
